import { Box, MenuItem, Select, TextField } from "@mui/material";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";
import React, { useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';

export interface SimplifiedRelativeDateSelectorProps {
  minDate?: dayjs.Dayjs | undefined;
  relativeDate: string, // "(+||-)P1D2W3M4YT5H6M7S(F||''||C)" - ISO8601 Relative Date String
  setRelativeDate: (relativeDate: string) => void,

  defaultRelativeDate?: string,
}

// The simplified relative date selector is a component that allows the user to select a relative date 
// using a simplified, single unit input.
export function SimplifiedRelativeDateSelector(props: SimplifiedRelativeDateSelectorProps): React.JSX.Element {
  const i18n  = useTranslation();

  const [validationText, setValidationText] = useState<string>("");

  const today = dayjs().startOf("day");

  const simplifiedRelativeDateUnits = useMemo(() => (
    [
      { value: "H", label: i18n.t("hours"), limit: props.minDate ? Math.floor(today.diff(props.minDate, "hours")) : undefined },
      { value: "D", label: i18n.t("days"), limit: props.minDate ? Math.floor(today.diff(props.minDate, "days")) : undefined },
      { value: "W", label: i18n.t("weeks"), limit: props.minDate ? Math.floor(today.diff(props.minDate, "weeks")) : undefined },
      { value: "M", label: i18n.t("months"), limit: props.minDate ? Math.floor(today.diff(props.minDate, "months")) : undefined },
      { value: "Y", label: i18n.t("years"), limit: props.minDate ? Math.floor(today.diff(props.minDate, "years")) : undefined },
    ]
  ), []).filter((unit) => (!unit.limit && unit.limit !== 0) || unit.limit > 0);;

  const { relativeDateValue, relativeDateUnit } = useMemo(() => {
    const relativeDate = props.relativeDate || props.defaultRelativeDate;
    if (relativeDate) {
      const relDateComponents = relativeDate.match(/PT*(\d+)([HDWMY])/);
      if (relDateComponents) {
        return {
          relativeDateValue: parseInt(relDateComponents[1]),
          relativeDateUnit: relDateComponents[2],
        };
      }
    }
    return {relativeDateValue: 0, relativeDateUnit: "D"};
  }, [props.relativeDate, props.defaultRelativeDate]);

  function setRelativeDateValue(value: number, unit?: string) {
    unit = unit || relativeDateUnit;
    const prefix = ("H" === unit) ? "T" : "";
    const unitType = simplifiedRelativeDateUnits.find((u) => u.value === unit);
    const limitedValue = unitType?.limit ? Math.min(unitType.limit, value) : value;
    if (limitedValue !== value) {
      setValidationText(i18n.t("widget-types.quick-search.global-date.warning-start-date-limit", {count: limitedValue, unit: unitType?.label}));
    } else {
      setValidationText("");
    }
    props.setRelativeDate(`-P${prefix}${value}${unit}`);
  }

  return (
    <Box sx={{display: "inline-block"}}>
      <TextField
        autoComplete="off"
        error={!!validationText}
        helperText={validationText}
        type="text"
        value={relativeDateValue || ""}
        onChange={(e) => {
          setRelativeDateValue(parseInt(e.target.value) || 0);
        }}
      />
      <Select
        value={relativeDateUnit}
        onChange={(e) => {
          setRelativeDateValue(relativeDateValue, e.target.value);
        }}
      >
        {simplifiedRelativeDateUnits.map((unit) => (
          <MenuItem key={unit.value} value={unit.value}>{unit.label}</MenuItem>
        ))}
      </Select>
    </Box>

  );
}